import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  ArrowRight,
  Package,
  Settings2,
  ShieldCheck,
  Zap,
} from 'lucide-react'
import { toast } from 'sonner'
import { toastApiError } from '@/shared/lib/api-error'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { Button } from '@/shared/components/ui/button'
import { DatePicker } from '@/shared/components/ui/date-picker'
import { Switch } from '@/shared/components/ui/switch'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { ConditionBuilder } from '@/admin/components/condition-builder'
import { useAvailableRefs } from '@/admin/components/condition-builder/use-available-refs'
import type { PredicateNode } from '@/admin/components/condition-builder/types'
import {
  useCreateAvailabilityRule,
  useUpdateAvailabilityRule,
  type AvailabilityRule,
} from '@/admin/hooks/use-availability-rules'
import type { Severity } from '@/admin/hooks/use-validation-rules'
import type {
  Product,
  ProductAttribute,
  ProductCategory,
  ProductTemplate,
} from '@/admin/types/products'
import { useWarrantyTemplates } from '@/admin/hooks/use-warranties'
import { useProductSearch, usePublishedProducts } from '@/admin/hooks/use-products'
import {
  SearchableSelect,
  type SearchableSelectGroup,
} from '@/shared/components/ui/searchable-select'
import { cn } from '@/shared/lib/utils'
import {
  EMPTY_ROUTING,
  makePolicyRuleProductCloneInput,
  readRouting,
  routingToEffectOverrides,
  type WarrantyRouting,
} from './policy-utils'

const SEVERITIES: { value: Severity; label: string; hint: string }[] = [
  { value: 'BLOCK', label: 'Block', hint: 'Hide non-matching products' },
  { value: 'WARN', label: 'Warn', hint: 'Show with a warning' },
  { value: 'INFO', label: 'Info', hint: 'Informational only' },
]

type DialogMode = 'create-policy' | 'add-mapping' | 'edit-mapping'

const policyMappingSchema = z.object({
  name: z.string(),
  description: z.string(),
  conditions: z.custom<PredicateNode | null>().nullable(),
  severity: z.enum(['BLOCK', 'WARN', 'INFO']),
  message: z.string(),
  effective_from: z.string(),
  effective_until: z.string(),
  is_active: z.boolean(),
  product_uuid: z.string(),
  routing: z.object({
    policy_code: z.string().trim().min(1, 'Policy number is required'),
    warranty_template_uuid: z
      .string()
      .trim()
      .min(1, 'Select the warranty template for this policy mapping.'),
    warranty_template_option_uuid: z
      .string()
      .trim()
      .min(1, 'Pick a warranty option for this policy mapping.'),
    priority: z.coerce.number(),
    attribute_values: z.record(z.string(), z.unknown()),
  }),
})

type PolicyMappingFormValues = z.infer<typeof policyMappingSchema>

const EMPTY: PolicyMappingFormValues = {
  name: '',
  description: '',
  conditions: null,
  // A warranty policy always carries a routing offer (template + option are
  // schema-required), and the resolver treats a BLOCK-severity rule as a veto
  // that contributes NO warranty plan (see ``_plans_from_hits``). So the offer
  // semantic is INFO ("informational only" - contributes a plan). Defaulting to
  // BLOCK silently hid every warranty created through this dialog.
  severity: 'INFO',
  message: '',
  effective_from: '',
  effective_until: '',
  is_active: true,
  product_uuid: '',
  routing: EMPTY_ROUTING,
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return <p className="mt-1 text-[11px] text-[var(--status-error)]">{message}</p>
}

function FormSection({
  icon: Icon,
  eyebrow,
  title,
  hint,
  children,
}: {
  icon: ComponentType<{ className?: string }>
  eyebrow: string
  title: string
  hint?: string
  children: ReactNode
}) {
  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-start gap-2.5">
        <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-2)]">
          <Icon className="h-3 w-3" />
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-[9px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]">
            {eyebrow}
          </span>
          <span className="text-[14px] font-medium leading-tight text-[var(--ink)]">
            {title}
          </span>
          {hint ? (
            <span className="text-[11px] leading-snug text-[var(--ink-3)]">
              {hint}
            </span>
          ) : null}
        </div>
      </header>
      <div className="pl-[34px]">{children}</div>
    </section>
  )
}

export function PolicyMappingDialog({
  open,
  onOpenChange,
  editing,
  sourceRule,
  mode,
  policyCode,
  existingPolicyCodes,
}: {
  attributes?: ProductAttribute[]
  productTemplates?: ProductTemplate[]
  productCategories?: ProductCategory[]
  open: boolean
  onOpenChange: (open: boolean) => void
  editing: AvailabilityRule | null
  sourceRule: AvailabilityRule | null
  mode: DialogMode
  policyCode?: string
  existingPolicyCodes: string[]
}) {
  const createRule = useCreateAvailabilityRule()
  const updateRule = useUpdateAvailabilityRule()
  const { refs, byRef, isLoading: refsLoading } = useAvailableRefs()
  const { data: warrantyTemplates = [] } = useWarrantyTemplates()
  const isEdit = mode === 'edit-mapping' && !!editing
  const isSimpleCreate = mode === 'create-policy'
  const isAddMapping = mode === 'add-mapping'

  const [showSettings, setShowSettings] = useState(false)
  const [productSearch, setProductSearch] = useState('')
  // Debounce the value sent to the API. The catalogue search casts
  // `product_data` JSON to text and does `icontains` (full scan), so we
  // only fire after the user pauses typing.
  const [debouncedProductSearch, setDebouncedProductSearch] = useState('')
  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedProductSearch(productSearch), 300)
    return () => window.clearTimeout(handle)
  }, [productSearch])
  const {
    control,
    getValues,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<PolicyMappingFormValues>({
    resolver: zodResolver(policyMappingSchema),
    defaultValues: EMPTY,
  })

  const form = useWatch({ control })
  const selectedProductUuid = form?.product_uuid ?? ''

  useEffect(() => {
    if (!open) {
      reset(EMPTY)
       
      setShowSettings(false)
      setProductSearch('')
      return
    }

    if (editing) {
      const routing = readRouting(editing.effect_overrides)
      reset({
        name: editing.name,
        description: editing.description ?? '',
        conditions: editing.conditions,
        severity: editing.severity,
        message: editing.message ?? '',
        effective_from: editing.effective_from ?? '',
        effective_until: editing.effective_until ?? '',
        is_active: editing.is_active,
        product_uuid: editing.scope_id ?? '',
        routing: {
          ...routing,
          policy_code: policyCode ?? routing.policy_code,
        },
      })
      setShowSettings(
        Boolean(
          editing.effective_from ||
            editing.effective_until ||
            editing.message ||
            !editing.is_active ||
            editing.severity !== 'BLOCK',
        ),
      )
      return
    }

    if (isAddMapping && sourceRule) {
      const routing = readRouting(sourceRule.effect_overrides)
      reset({
        name: sourceRule.name,
        description: sourceRule.description ?? '',
        conditions: sourceRule.conditions,
        severity: sourceRule.severity,
        message: sourceRule.message ?? '',
        effective_from: sourceRule.effective_from ?? '',
        effective_until: sourceRule.effective_until ?? '',
        is_active: sourceRule.is_active,
        product_uuid: '',
        routing: {
          ...routing,
          policy_code: policyCode ?? routing.policy_code,
        },
      })
      setShowSettings(false)
      setProductSearch('')
      return
    }

    reset({
      ...EMPTY,
      routing: {
        ...EMPTY_ROUTING,
        policy_code: policyCode ?? '',
      },
    })
    setShowSettings(false)
    setProductSearch('')
  }, [editing, isAddMapping, open, policyCode, reset, sourceRule])

  function updateRouting(patch: Partial<WarrantyRouting>) {
    setValue(
      'routing',
      { ...getValues('routing'), ...patch },
      { shouldDirty: true, shouldValidate: true },
    )
  }

  function updateRoutingAttribute(code_name: string, value: unknown) {
    const next = { ...getValues('routing').attribute_values }
    if (value === '' || value === null || value === undefined) {
      delete next[code_name]
    } else {
      next[code_name] = value
    }
    updateRouting({ attribute_values: next })
  }

  // Policy mappings target product *lines* (the VARIANT_BASE parent or a
  // standalone product), not individual variants - mapping at the line
  // covers every SKU in it. The backend's `search` query still surfaces a
  // parent line when one of its variants' SKUs matches, so searching by
  // variant SKU continues to work even though variants themselves are
  // excluded from the result.
  const { data: searchedProductsPage, isLoading: productSearchLoading } =
    useProductSearch(debouncedProductSearch)
  const searchedProducts = useMemo<Product[]>(
    () => (searchedProductsPage?.results ?? []) as Product[],
    [searchedProductsPage?.results],
  )

  const { data: addMappingProductsPage, isLoading: addMappingProductsLoading } =
    usePublishedProducts(
      isAddMapping
        ? {
            page_size: '100',
            search: debouncedProductSearch.trim() || undefined,
          }
        : undefined,
    )
  const addMappingAllProducts = useMemo<Product[]>(
    () => (addMappingProductsPage?.results ?? []) as Product[],
    [addMappingProductsPage?.results],
  )
  const addMappingProducts = addMappingAllProducts

  const productOptionsSource = isAddMapping ? addMappingProducts : searchedProducts

  // Persistent cache of every product we've ever loaded in this dialog
  // session. Needed because the SearchableSelect clears `productSearch` on
  // selection, which refetches the catalogue without a search term - the
  // selected product can fall outside the first page and disappear from
  // `productOptionsSource`, leaving the trigger blank. Holding onto seen
  // products keeps the selected label stable across search resets.
  const [seenProducts, setSeenProducts] = useState<Map<string, Product>>(
    () => new Map(),
  )
  useEffect(() => {
     
    setSeenProducts((prev) => {
      let changed = false
      const next = new Map(prev)
      for (const product of addMappingAllProducts) {
        if (next.get(product.uuid) !== product) {
          next.set(product.uuid, product)
          changed = true
        }
      }
      for (const product of searchedProducts) {
        if (next.get(product.uuid) !== product) {
          next.set(product.uuid, product)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [addMappingAllProducts, searchedProducts])

  // Map for parent-product / metadata lookup. We include VARIANT_BASE rows so
  // we can render the parent name on each variant option even though bases
  // aren't selectable.
  const productByUuid = seenProducts

  const productDisplay = useMemo(() => {
    function getSku(product: Product): string {
      const raw = product.product_data?.sku
      return typeof raw === 'string' || typeof raw === 'number'
        ? String(raw)
        : ''
    }
    function getParent(product: Product): string {
      if (product.parent_product_name) return product.parent_product_name
      const parentUuid = product.parent_product_uuid
      if (!parentUuid) return ''
      return productByUuid.get(parentUuid)?.cached_short_name ?? ''
    }
    function getPrimary(product: Product): string {
      const parent = getParent(product)
      const name =
        product.cached_short_name || product.cached_long_name || product.uuid
      if (parent && name.startsWith(`${parent} - `)) {
        return name.slice(parent.length + 3) || name
      }
      return name
    }
    return { getSku, getParent, getPrimary }
  }, [productByUuid])

  const productGroups = useMemo<SearchableSelectGroup[]>(() => {
    const rows = [...productOptionsSource]
    // Make sure the currently selected product is always in the list so the
    // trigger label resolves, even after the search input was cleared and
    // the catalogue refetched without it.
    if (
      selectedProductUuid &&
      !rows.some((p) => p.uuid === selectedProductUuid)
    ) {
      const cached = seenProducts.get(selectedProductUuid)
      if (cached) rows.push(cached)
    }
    rows.sort((a, b) =>
      (a.cached_short_name ?? a.cached_long_name ?? '').localeCompare(
        b.cached_short_name ?? b.cached_long_name ?? '',
      ),
    )
    if (rows.length === 0) return []
    return [
      {
        label: 'Products',
        options: rows.map((product) => {
          const primary = productDisplay.getPrimary(product)
          const parent = productDisplay.getParent(product)
          const sku = productDisplay.getSku(product)
          const template = product.product_template?.name ?? ''
          // Compact label used by the trigger when selected. Includes every
          // discriminator so two variants with the same name don't collide.
          const labelParts = [primary]
          const meta: string[] = []
          if (parent) meta.push(parent)
          if (sku) meta.push(`SKU ${sku}`)
          if (meta.length) labelParts.push(`(${meta.join(' · ')})`)
          if (template) labelParts.push(`- ${template}`)
          return {
            value: product.uuid,
            label: labelParts.join(' '),
            searchText: [
              primary,
              parent,
              sku,
              template,
              product.cached_short_name,
              product.cached_long_name,
            ]
              .filter(Boolean)
              .join(' '),
          }
        }),
      },
    ]
  }, [productOptionsSource, productDisplay, selectedProductUuid, seenProducts])

  const renderProductOption = (
    option: { value: string; label: string },
    { selected }: { selected: boolean },
  ) => {
    const product = productByUuid.get(option.value)
    if (!product) {
      return (
        <span className="truncate text-[13px] text-[var(--ink)]">
          {option.label}
        </span>
      )
    }
    const primary = productDisplay.getPrimary(product)
    const parent = productDisplay.getParent(product)
    const sku = productDisplay.getSku(product)
    const template = product.product_template?.name ?? ''
    const subtitleParts = [parent, template].filter(Boolean)
    return (
      <div className="flex min-w-0 flex-col gap-0.5">
        <span
          className={cn(
            'flex items-baseline gap-2 truncate text-[13px]',
            selected ? 'text-[var(--green)]' : 'text-[var(--ink)]',
          )}
        >
          <span className="truncate">{primary}</span>
          {sku ? (
            <span className="shrink-0 text-[10px] text-[var(--ink-3)]">
              SKU {sku}
            </span>
          ) : null}
        </span>
        {subtitleParts.length > 0 ? (
          <span className="truncate text-[11px] text-[var(--ink-3)]">
            {subtitleParts.join(' · ')}
          </span>
        ) : null}
      </div>
    )
  }

  const selectedProduct = useMemo(
    () =>
      productOptionsSource.find((product) => product.uuid === selectedProductUuid) ??
      (selectedProductUuid ? seenProducts.get(selectedProductUuid) ?? null : null),
    [productOptionsSource, selectedProductUuid, seenProducts],
  )

  const selectedWarrantyTemplate = useMemo(
    () =>
      warrantyTemplates.find(
        (template) => template.uuid === form.routing.warranty_template_uuid,
      ) ?? null,
    [form.routing.warranty_template_uuid, warrantyTemplates],
  )

  const warrantyOptions = selectedWarrantyTemplate?.options ?? []
  const warrantyAttributes = selectedWarrantyTemplate?.attributes ?? []
  const hasRouting = Boolean(
    form.routing.warranty_template_uuid &&
      form.routing.warranty_template_option_uuid,
  )
  const normalizedExistingCodes = useMemo(
    () =>
      new Set(
        existingPolicyCodes
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean),
      ),
    [existingPolicyCodes],
  )

  const isPolicyCodeLocked = mode !== 'create-policy'

  const handleSave = handleSubmit(
    async (values) => {
      const nextPolicyCode = values.routing.policy_code.trim().toUpperCase()
      if (
        mode === 'create-policy' &&
        normalizedExistingCodes.has(nextPolicyCode.toLowerCase())
      ) {
        toast.error('Policy number must be unique')
        return
      }
      if (!isSimpleCreate && !values.product_uuid) {
        toast.error('Pick a product to map this policy to.')
        return
      }
      if (isAddMapping && !sourceRule) {
        toast.error('No source rule found for this policy.')
        return
      }

      if (isAddMapping && sourceRule) {
        try {
          await createRule.mutateAsync(
            makePolicyRuleProductCloneInput(
              sourceRule,
              nextPolicyCode,
              values.product_uuid,
            ),
          )
          toast.success('Product mapping created')
          reset(EMPTY)
          onOpenChange(false)
        } catch (err) {
          toastApiError(err, 'Failed to save policy mapping')
        }
        return
      }

      const payload = {
        name: isSimpleCreate ? `Policy ${nextPolicyCode}` : values.name.trim(),
        description: values.description.trim(),
        conditions: isSimpleCreate ? null : values.conditions,
        // Offer-carrying policy rules must be INFO, not BLOCK: BLOCK vetoes
        // availability and contributes no warranty plan, so a "simple create"
        // policy stamped BLOCK could never surface its own warranty.
        severity: isSimpleCreate ? ('INFO' as const) : values.severity,
        message: isSimpleCreate ? null : values.message.trim() || null,
        priority: values.routing.priority || 0,
        scope: isSimpleCreate ? ('warranty_filter' as const) : ('product' as const),
        scope_id: isSimpleCreate ? null : values.product_uuid,
        effect_overrides: routingToEffectOverrides({
          ...values.routing,
          policy_code: nextPolicyCode,
        }),
        query_filters: {},
        effective_from: values.effective_from || null,
        effective_until: values.effective_until || null,
        is_active: values.is_active,
      }

      try {
        if (isEdit && editing) {
          await updateRule.mutateAsync({ uuid: editing.uuid, ...payload })
          toast.success('Policy mapping updated')
        } else {
          await createRule.mutateAsync(payload)
          toast.success(
            mode === 'create-policy' ? 'Policy created' : 'Policy mapping created',
          )
        }
        reset(EMPTY)
        onOpenChange(false)
      } catch (err) {
        toastApiError(
          err,
          isEdit ? 'Failed to update policy mapping' : 'Failed to save policy mapping',
        )
      }
    },
    (invalid) => {
      const firstError =
        invalid.routing?.policy_code?.message ||
        (!isSimpleCreate ? invalid.name?.message : undefined) ||
        invalid.routing?.warranty_template_uuid?.message ||
        invalid.routing?.warranty_template_option_uuid?.message ||
        (!isSimpleCreate ? invalid.product_uuid?.message : undefined)
      toast.error(firstError ?? 'Please fix the highlighted fields.')
    },
  )

  const isPending = createRule.isPending || updateRule.isPending
  const dialogTitle =
    mode === 'create-policy'
      ? 'New policy'
      : mode === 'add-mapping'
        ? 'Add product'
        : 'Edit policy mapping'
  const dialogDescription =
    mode === 'create-policy'
      ? 'Create a policy shell with warranty coverage and timing. Product mappings can be added afterwards.'
      : mode === 'add-mapping'
        ? 'Choose the product to add to this policy. We will copy the existing policy rule and attach it to the selected product.'
      : 'Configure how this policy maps to products and warranty coverage.'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        bare
        className={cn(
          'max-h-[min(86vh,900px)]',
          isAddMapping ? 'sm:max-w-[420px]' : 'sm:max-w-[680px]',
        )}
      >
        <DialogHeader className="flex flex-row items-start gap-3 border-b-[0.5px] border-[var(--rule)] px-6 pt-5 pb-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-2)]">
            <ShieldCheck className="h-4 w-4" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <DialogTitle className="text-[16px] leading-tight">
              {dialogTitle}
            </DialogTitle>
            <DialogDescription className="text-[12px] leading-snug text-[var(--ink-3)]">
              {dialogDescription}
            </DialogDescription>
          </div>
        </DialogHeader>

        <DialogBody className="gap-6">
          {isAddMapping ? (
            <div className="flex flex-col gap-3 py-2">
              <Label className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Product
              </Label>
              <Controller
                control={control}
                name="product_uuid"
                render={({ field }) => (
                  <SearchableSelect
                    value={field.value}
                    onValueChange={field.onChange}
                    groups={productGroups}
                    placeholder="Select a product…"
                    searchPlaceholder="Filter products…"
                    searchValue={productSearch}
                    onSearchChange={setProductSearch}
                    loading={addMappingProductsLoading}
                    emptyMessage="No matching products"
                    triggerClassName="h-9 w-full text-[13px]"
                    renderOption={renderProductOption}
                  />
                )}
              />
              <FieldError message={errors.product_uuid?.message} />

              {selectedProduct ? (
                <div className="rounded-[6px] border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] px-3 py-2 text-[12px] text-[var(--green)]">
                  Adding product mapping for{' '}
                  <span className="font-medium">
                    {selectedProduct.cached_short_name || selectedProduct.cached_long_name}
                  </span>
                  .
                </div>
              ) : null}
            </div>
          ) : (
            <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                Policy number
              </Label>
              <Controller
                control={control}
                name="routing.policy_code"
                render={({ field }) => (
                  <Input
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                    disabled={isPolicyCodeLocked}
                    placeholder="e.g. 10020608M"
                    className="h-9 text-[13px]"
                  />
                )}
              />
              <FieldError message={errors.routing?.policy_code?.message} />
            </div>
            {!isSimpleCreate ? (
              <div>
                <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                  Mapping name
                </Label>
                <Controller
                  control={control}
                  name="name"
                  render={({ field }) => (
                    <Input
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="e.g. Residential detached homes"
                      className="h-9 text-[13px]"
                    />
                  )}
                />
                <FieldError message={errors.name?.message} />
              </div>
            ) : null}
          </div>

          <div>
            <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
              Description
            </Label>
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <Textarea
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Optional admin note explaining when this mapping should apply"
                  className="min-h-[64px] text-[13px]"
                />
              )}
            />
          </div>

          {!isSimpleCreate ? (
            <>
              <div className="-mx-6 border-t-[0.5px] border-[var(--rule)]" />

              <FormSection
                icon={Zap}
                eyebrow="When"
                title="Match these registration conditions"
                hint="All conditions must be met for the policy mapping to apply."
              >
                {refsLoading ? (
                  <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-4 text-center text-[12px] text-[var(--ink-3)]">
                    Loading fields...
                  </div>
                ) : (
                  <ConditionBuilder
                    value={form.conditions}
                    onChange={(next) =>
                      setValue('conditions', next, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                    availableRefs={refs}
                    byRef={byRef}
                  />
                )}
              </FormSection>

              <div className="flex items-center gap-2 pl-[34px] text-[var(--ink-3)]">
                <div className="h-[0.5px] flex-1 bg-[var(--rule)]" />
                <ArrowRight className="h-3 w-3" />
                <div className="h-[0.5px] flex-1 bg-[var(--rule)]" />
              </div>

              <FormSection
                icon={Package}
                eyebrow="Maps product"
                title="Map this policy to a product"
                hint="Each policy mapping covers exactly one product. Add more products to the same policy from the policies list."
              >
                <div className="flex flex-col gap-2 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]/40 p-4">
                  <Label className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                    Product
                  </Label>
                  <Controller
                    control={control}
                    name="product_uuid"
                    render={({ field }) => (
                      <SearchableSelect
                        value={field.value}
                        onValueChange={field.onChange}
                        groups={productGroups}
                        placeholder="Search and choose product"
                        searchPlaceholder="Search products..."
                        searchValue={productSearch}
                        onSearchChange={setProductSearch}
                        loading={productSearchLoading}
                        emptyMessage={
                          productSearch.trim().length < 2
                            ? 'Type at least 2 characters to search products'
                            : 'No matching products'
                        }
                        renderOption={renderProductOption}
                      />
                    )}
                  />
                  <FieldError message={errors.product_uuid?.message} />
                </div>
              </FormSection>
            </>
          ) : null}

          <div className="-mx-6 border-t-[0.5px] border-[var(--rule)]" />

          <FormSection
            icon={ShieldCheck}
            eyebrow="Issues"
            title="Warranty coverage"
            hint="Each mapping must point at the warranty template and option that should be issued."
          >
            <div className="flex flex-col gap-4 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]/40 p-4">
              {isSimpleCreate ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="min-w-0">
                    <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                      Start date
                    </Label>
                    <Controller
                      control={control}
                      name="effective_from"
                      render={({ field }) => (
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="No start date"
                          className="text-[13px]"
                        />
                      )}
                    />
                  </div>
                  <div className="min-w-0">
                    <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                      End date
                    </Label>
                    <Controller
                      control={control}
                      name="effective_until"
                      render={({ field }) => (
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="No end date"
                          className="text-[13px]"
                        />
                      )}
                    />
                  </div>
                  <div className="min-w-0">
                    <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                      Priority
                    </Label>
                    <Controller
                      control={control}
                      name="routing.priority"
                      render={({ field }) => (
                        <Input
                          type="number"
                          inputMode="numeric"
                          value={String(field.value)}
                          onChange={(e) => {
                            const parsed = Number(e.target.value)
                            field.onChange(Number.isFinite(parsed) ? parsed : 0)
                          }}
                          placeholder="0"
                          className="h-9 text-[13px]"
                        />
                      )}
                    />
                  </div>
                </div>
              ) : null}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="min-w-0">
                  <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                    Warranty template
                  </Label>
                  <Select
                    value={form.routing.warranty_template_uuid || '__none__'}
                    onValueChange={(value: string) => {
                      const next = value === '__none__' ? '' : value
                      updateRouting({
                        warranty_template_uuid: next,
                        warranty_template_option_uuid: '',
                        attribute_values: {},
                      })
                    }}
                  >
                    <SelectTrigger className="h-9 w-full text-[13px]">
                      <SelectValue placeholder="Choose template" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Select template</SelectItem>
                      {warrantyTemplates.map((template) => (
                        <SelectItem key={template.uuid} value={template.uuid}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError
                    message={errors.routing?.warranty_template_uuid?.message}
                  />
                </div>
                <div className="min-w-0">
                  <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                    Option
                  </Label>
                  <Select
                    value={form.routing.warranty_template_option_uuid || '__none__'}
                    onValueChange={(value: string) =>
                      updateRouting({
                        warranty_template_option_uuid:
                          value === '__none__' ? '' : value,
                      })
                    }
                  >
                    <SelectTrigger
                      className="h-9 w-full text-[13px]"
                      disabled={!form.routing.warranty_template_uuid}
                    >
                      <SelectValue
                        placeholder={
                          form.routing.warranty_template_uuid
                            ? 'Choose option'
                            : 'Pick template first'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Select option</SelectItem>
                      {warrantyOptions.map((option) => (
                        <SelectItem key={option.uuid} value={option.uuid}>
                          {option.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError
                    message={errors.routing?.warranty_template_option_uuid?.message}
                  />
                </div>
              </div>

              {!isSimpleCreate ? (
                <div>
                  <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                    Priority
                  </Label>
                  <Controller
                    control={control}
                    name="routing.priority"
                    render={({ field }) => (
                      <Input
                        type="number"
                        inputMode="numeric"
                        value={String(field.value)}
                        onChange={(e) => {
                          const parsed = Number(e.target.value)
                          field.onChange(Number.isFinite(parsed) ? parsed : 0)
                        }}
                        placeholder="0"
                        className="h-9 text-[13px]"
                      />
                    )}
                  />
                </div>
              ) : null}

              {form.routing.warranty_template_uuid ? (
                <div>
                  <div className="mb-2 flex items-baseline gap-2">
                    <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                      Coverage value overrides
                    </span>
                    {Object.keys(form.routing.attribute_values).length > 0 ? (
                      <span className="text-[10px] tabular-nums text-[var(--ink-3)]">
                        {Object.keys(form.routing.attribute_values).length} set
                      </span>
                    ) : null}
                  </div>
                  {warrantyAttributes.length === 0 ? (
                    <p className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg)] px-3 py-2.5 text-[12px] text-[var(--ink-3)]">
                      This template has no attributes to override.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-1.5">
                      {warrantyAttributes.map((attr) => (
                        <div
                          key={attr.uuid}
                          className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-center gap-1.5 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-1.5"
                        >
                          <div className="px-2 text-[13px] text-[var(--ink-2)]">
                            {attr.display_name}
                            <span className="ml-1 text-[11px] text-[var(--ink-3)]">
                              ({attr.code_name})
                            </span>
                          </div>
                          <Input
                            value={String(
                              form.routing.attribute_values[attr.code_name] ?? '',
                            )}
                            onChange={(e) =>
                              updateRoutingAttribute(attr.code_name, e.target.value)
                            }
                            placeholder="Use template default"
                            className="h-8 border-0 bg-transparent text-[13px] focus-visible:ring-0"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </FormSection>

          {!isSimpleCreate ? (
            <>
              <div className="-mx-6 border-t-[0.5px] border-[var(--rule)]" />

              <section className="flex flex-col gap-3">
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => setShowSettings((value) => !value)}
                  className="h-auto self-start p-0 text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
                >
                  <Settings2 className="h-3 w-3" />
                  {showSettings ? 'Hide settings' : 'Show settings'}
                </Button>

                {showSettings ? (
                  <div className="flex flex-col gap-4 rounded-[8px] border-[0.5px] border-[var(--rule)] p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex flex-col">
                        <span className="text-[13px] text-[var(--ink)]">
                          Mapping active
                        </span>
                        <span className="text-[11px] text-[var(--ink-3)]">
                          When off, this mapping is ignored without deleting it.
                        </span>
                      </div>
                      <Switch
                        checked={form.is_active}
                        aria-label="Toggle mapping active"
                        onCheckedChange={(checked) =>
                          setValue('is_active', checked, {
                            shouldDirty: true,
                          })
                        }
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                          Effective from
                        </Label>
                        <Controller
                          control={control}
                          name="effective_from"
                          render={({ field }) => (
                            <DatePicker
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="No start date"
                              className="text-[13px]"
                            />
                          )}
                        />
                      </div>
                      <div>
                        <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                          Effective until
                        </Label>
                        <Controller
                          control={control}
                          name="effective_until"
                          render={({ field }) => (
                            <DatePicker
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="No end date"
                              className="text-[13px]"
                            />
                          )}
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                        Severity
                      </Label>
                      <div className="flex flex-wrap gap-1.5">
                        {SEVERITIES.map((severity) => {
                          const active = form.severity === severity.value
                          return (
                            <Button
                              key={severity.value}
                              variant="ghost"
                              type="button"
                              onClick={() =>
                                setValue('severity', severity.value, {
                                  shouldDirty: true,
                                })
                              }
                              className={cn(
                                'h-auto flex-col items-start gap-0.5 rounded-[6px] border-[0.5px] px-3 py-2 text-left transition-all duration-150',
                                active
                                  ? 'border-[var(--green)] bg-[var(--green)]/5 text-[var(--ink)]'
                                  : 'border-[var(--rule)] hover:border-[var(--rule-strong)]',
                              )}
                            >
                              <span className="text-[12px] font-medium leading-tight">
                                {severity.label}
                              </span>
                              <span className="text-[10px] leading-tight text-[var(--ink-3)]">
                                {severity.hint}
                              </span>
                            </Button>
                          )
                        })}
                      </div>
                    </div>

                    <div>
                      <Label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                        Message
                      </Label>
                      <Controller
                        control={control}
                        name="message"
                        render={({ field }) => (
                          <Input
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Shown when the mapping fires (optional)"
                            className="h-9 text-[13px]"
                          />
                        )}
                      />
                    </div>
                  </div>
                ) : null}
              </section>
            </>
          ) : null}
            </>
          )}
        </DialogBody>

        <DialogFooter className="flex items-center justify-between gap-2 border-t-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]/40 px-6 py-3 sm:flex-row sm:justify-between">
          <p className="text-[11px] text-[var(--ink-3)]">
            {!hasRouting
              ? 'Select the warranty template and option.'
              : !isSimpleCreate && !form.product_uuid
                ? 'Pick a product to map this policy to.'
                : 'Ready to save.'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="h-9 px-3 text-[12px]"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={isPending || (isAddMapping && !form.product_uuid)}
              className="h-9 cursor-pointer rounded-[6px] bg-[var(--green)] px-4 text-[12px] font-medium text-[var(--bg)] hover:bg-[var(--green-hover)] disabled:opacity-40"
            >
              {mode === 'edit-mapping'
                ? 'Save changes'
                : mode === 'create-policy'
                  ? 'Create policy'
                  : 'Add product'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default PolicyMappingDialog
